/**
 * A per-browser secret identifying whoever created a pin. It is generated on
 * first visit and kept in localStorage; the server only ever stores its SHA-256
 * hash, and compares against it to decide who may edit or delete a pin.
 *
 * This is an ownership check, not authentication: anyone can mint a token, and
 * clearing site data means losing control of the pins created with it.
 */
const STORAGE_KEY = 'info-traflic:owner-token'

function generateToken(): string {
  const bytes = new Uint8Array(32)
  if (globalThis.crypto?.getRandomValues) {
    globalThis.crypto.getRandomValues(bytes)
  } else {
    // `crypto.randomUUID` needs a secure context, `getRandomValues` does not,
    // so this branch should only ever be reached on very old browsers.
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

let cachedToken: string | null = null

export function getOwnerToken(): string {
  if (cachedToken) return cachedToken

  let token: string | null = null
  try {
    token = localStorage.getItem(STORAGE_KEY)
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). The token
    // then lives for the page session only, which still works for the visitor
    // who just created a pin.
  }

  if (!token) {
    token = generateToken()
    try {
      localStorage.setItem(STORAGE_KEY, token)
    } catch {
      // See above — fall back to an in-memory token.
    }
  }

  cachedToken = token
  return token
}

export const OWNER_TOKEN_HEADER = 'X-Owner-Token'
